import type { FC, ReactNode } from 'react'
import { Briefcase, CheckCircle2, PenLine, Clock3, Archive } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Loader } from '@/components/ui'
import { useJobStats } from '@/lib/api/hooks/job'

interface StatsCardProps {
	title: string
	value: string | number
	icon: ReactNode
	iconContainerClassName: string
	iconClassName: string
}

interface StatConfig {
	key: 'total' | 'totalPublished' | 'totalDrafts' | 'totalEndingSoon' | 'totalClosed'
	title: string
	icon: LucideIcon
	iconContainerClassName: string
	iconClassName: string
}

const StatCard = ({
	title,
	value,
	icon,
	iconContainerClassName,
	iconClassName,
}: StatsCardProps) => {
	return (
		<article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-12 w-12 items-center justify-center rounded-md ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div>
					<p className="text-base font-medium text-gray-600">{title}</p>
					<p className="text-[2rem] leading-[1.1] font-semibold text-gray-900">{value}</p>
				</div>
			</div>
		</article>
	)
}

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'total',
		title: 'Total Jobs',
		icon: Briefcase,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
	},
	{
		key: 'totalPublished',
		title: 'Published',
		icon: CheckCircle2,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
	},
	{
		key: 'totalDrafts',
		title: 'Drafts',
		icon: PenLine,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#B86A00]',
	},
	{
		key: 'totalEndingSoon',
		title: 'Ending Soon',
		icon: Clock3,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#C2650A]',
	},
	{
		key: 'totalClosed',
		title: 'Closed',
		icon: Archive,
		iconContainerClassName: 'bg-[#F3F4F680]',
		iconClassName: 'text-[#6B7280]',
	},
]

const JobsStats: FC = () => {
	const { data: stats, isLoading, isError, refetch } = useJobStats()

	if (isError) {
		return (
			<div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4 sm:col-span-2 xl:col-span-5">
				<p>Error loading statistics.</p>
				<button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">
					Retry
				</button>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center">
				<Loader size="md" variant="wine" message="Loading statistics..." />
			</div>
		)
	}

	const values = {
		total: stats?.total ?? 0,
		totalPublished: stats?.totalPublished ?? 0,
		totalDrafts: stats?.totalDrafts ?? 0,
		totalEndingSoon: stats?.totalEndingSoon ?? 0,
		totalClosed: stats?.totalClosed ?? 0,
	}

	return (
		<section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 2xl:w-3/4">
			{STATS_CONFIG.map((config) => {
				const Icon = config.icon
				return (
					<StatCard
						key={config.key}
						title={config.title}
						value={values[config.key]}
						icon={<Icon className="w-6 h-6" />}
						iconContainerClassName={config.iconContainerClassName}
						iconClassName={config.iconClassName}
					/>
				)
			})}
		</section>
	)
}

export default JobsStats
